import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Start
} from "@mui/icons-material";
import { RemoveButton } from "../modals";
import { useWindowSize } from "../../core";
import { PontoEncontro, PontoEncontroData } from "../zas";

interface PontosEncontroTableProps {
  isAdd?: boolean;
  isEditable?: boolean;
  initPontos?: PontoEncontro[];
  onChoose?: (ponto: PontoEncontro) => void;
  onChange?: (pontos: PontoEncontro[]) => void;
}

export const PontosEncontroTable: React.FC<PontosEncontroTableProps> = ({
  onChange,
  onChoose,
  isAdd,
  isEditable,
  initPontos,
}) => {
  const { height, width } = useWindowSize();
  const theme = useTheme();

  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [filterSize, setFilterSize] = useState({ width: 0, height: 0 });
  const [titleSize, setTitleSize] = useState({ width: 0, height: 0 });
  const [pontos, setPontos] = useState<PontoEncontro[]>(initPontos ?? []);
  const [newPonto, setNewPonto] = useState<{
    lat: number | string;
    lon: number | string;
    ponto: string; // Apenas com 50 caracteres
    popEstimada: number;
    areaUtil: number | string;
    obs?: string;
  }>({
    lat: 0,
    lon: 0,
    ponto: "",
    popEstimada: 0,
    areaUtil: 0,
  });


  // Tamanho das opções da tabela
  useEffect(() => {
    if (divRef.current) {
      const { width, height } = divRef.current.getBoundingClientRect();
      setSize({ width, height });
    }
    if (filterRef.current) {
      const { width, height } = filterRef.current.getBoundingClientRect();
      setFilterSize({ width, height });
    }
    if (titleRef.current) {
      const { width, height } = titleRef.current.getBoundingClientRect();
      setTitleSize({ width, height });
    }
  }, [divRef, filterRef, titleRef, height, width]);


  const handleAddPonto = () => {
    if (
      typeof newPonto.lat === "string" ||
      typeof newPonto.lon === "string" ||
      typeof newPonto.areaUtil === "string" ||
      newPonto.lat === 0 ||
      newPonto.lon === 0 ||
      newPonto.areaUtil === 0 ||
      newPonto.ponto === "" ||
      newPonto.ponto.length > 45
    ) return;

    const ponto = new PontoEncontro(
      {
        ...newPonto,
        id: 0,
        idZas: 0,
        dataCadastro: new Date(Date.now()),
      } as PontoEncontroData,
      theme.palette.geometries.pontoEncontro,
    );

    const newPontos = [...pontos];
    newPontos.push(ponto);
    if (onChange) onChange(newPontos);
    setPontos(newPontos);
    setNewPonto({ lat: 0, lon: 0, ponto: "", popEstimada: 0, areaUtil: 0, obs: "" });
  };

  const onRemove = (unidade: PontoEncontro) => {
    const newPontos = pontos.filter((ed) => ed !== unidade);
    setPontos([...newPontos]);
    if (onChange) onChange(newPontos);
  };

  return <Box
    ref={divRef}
    sx={{
      flex: 1,
      width: "100%",
      height: "100%",
      padding: "15px",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    {/* Título */}
    <Box
      ref={titleRef}
      sx={{
        width: "100%",
        paddingTop: "7px",
        paddingX: "15px",
        display: "flex",
        flexDirection: "row",
        alignItems: "start",
        justifyContent: "start",
      }}>
      <Typography fontSize={18} fontWeight={"bold"}>
        {"Pontos de Encontro"}
      </Typography>
    </Box>

    {/* Novo ponto*/}
    <div
      ref={filterRef}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Novo Ponto */}
      {isAdd &&
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          paddingX: "15px",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
        }}>
          <Box sx={{
            flex: 1,
            display: "flex",
            paddingX: "15px",
            flexDirection: "column",
          }}>

            <Box sx={{
              gap: "15px",
              display: "flex",
              flexDirection: "row",
              alignIntems: "center",
              justifyContent: "center",
            }}>

              {/* Nome */}
              <Box sx={{
                minWidth: "150px",
                padding: "0",
                display: "flex",
                alignIntems: "center",
                flexDirection: "column",
                justifyContent: "center",
              }}>
                <TextField
                  fullWidth
                  name="name_ponto"
                  variant="standard"
                  label="Nome do Ponto"
                  value={newPonto.ponto}
                  onChange={(event) => setNewPonto({ ...newPonto, ponto: event.target.value })}
                  slotProps={{ htmlInput: { maxLength: 45 } }}
                />
                <Typography
                  variant="caption"
                  color={newPonto.ponto.length > 42 ? "error" : (newPonto.ponto.length > 30 ? "warning" : undefined)}
                >
                  {newPonto.ponto.length}/45 caracteres
                </Typography>
              </Box>

              {/* Lat */}
              <TextField
                fullWidth
                label="Latitude"
                name="lat"
                value={newPonto.lat.toString().replaceAll(".", ",")}
                onChange={(event) => {
                  const strVal = event.target.value.replaceAll(",", ".");

                  let val: number | string = strVal;
                  if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                    val = Number(strVal);
                    if (isNaN(val)) return;
                  }
                  setNewPonto({
                    ...newPonto,
                    lat: val,
                  });
                }}
                variant="standard"
              />

              {/* Lng */}
              <TextField
                fullWidth
                label="Longitude"
                name="lng"
                value={newPonto.lon.toString().replaceAll(".", ",")}
                onChange={(event) => {
                  const strVal = event.target.value.replaceAll(",", ".");

                  let val: number | string = strVal;
                  if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                    val = Number(strVal);
                    if (isNaN(val)) return;
                  }
                  setNewPonto({
                    ...newPonto,
                    lon: val,
                  });
                }}
                variant="standard"
              />

              {/* População estimada */}
              <TextField
                fullWidth
                label="População Estimada"
                name="pop_est"
                value={newPonto.popEstimada.toString()}
                onChange={(event) => {
                  const val = Number(event.target.value);
                  if (isNaN(val)) return;
                  setNewPonto({
                    ...newPonto,
                    popEstimada: val,
                  });
                }}
                variant="standard"
              />

              {/* Áera útil */}
              <TextField
                fullWidth
                label="Área Útil (m²)"
                name="area_util"
                value={newPonto.areaUtil.toString().replaceAll(".", ",")}
                onChange={(event) => {
                  const strVal = event.target.value.replaceAll(",", ".");

                  let val: number | string = strVal;
                  if (strVal !== "" && strVal !== "-" && !strVal.endsWith(".")) {
                    val = Number(strVal);
                    if (isNaN(val)) return;
                    if (val < 0) val = -val;
                  }
                  setNewPonto({
                    ...newPonto,
                    areaUtil: val,
                  });
                }}
                variant="standard"
              />

            </Box>

            {/* Observações */}
            <TextField
              multiline
              fullWidth
              name="obs_ponto"
              variant="standard"
              label="Observações"
              value={newPonto.obs}
              onChange={(event) => setNewPonto({ ...newPonto, obs: event.target.value })}
            />

          </Box>

          {/* BTN Add */}
          <Box sx={{
            width: "35px",
            height: "35px",
          }}>
            <Tooltip title={"Adicionar Ponto de Encontro"}>
              <IconButton onClick={handleAddPonto}>
                <Add />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>
      }

      <div style={{ height: "15px" }} />
      <Divider />
      <div style={{ height: "15px" }} />
    </div >

    {/* Tabela de estações */}
    < TableContainer sx={{
      flex: 1,
      overflow: "auto",
      height: size.height - filterSize.height - titleSize.height,
      width: size.width
    }}>
      <Table
        stickyHeader
        size="small"
        aria-label="sticky table"
      >
        <TableHead>
          <TableRow>
            {onChoose && <TableCell align="left">Selecionar</TableCell>}
            <TableCell align="left">Nome</TableCell>
            <TableCell align="left">Latitude</TableCell>
            <TableCell align="left">Longitude</TableCell>
            <TableCell align="center">População</TableCell>
            <TableCell align="center">Área Útil (m²)</TableCell>
            <TableCell align="center">Observações</TableCell>
            {isEditable && <TableCell align="center">Remover</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {pontos.map((row, i) => (
            <TableRow
              key={i}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {onChoose && <TableCell align="left">
                <IconButton onClick={() => onChoose(row)} >
                  <Start />
                </IconButton>
              </TableCell>}
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.properties.ponto}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lat.toString().replaceAll(".", ",")}</TableCell>
              <TableCell align="left" sx={{ textOverflow: "ellipsis" }} >{row.getLatLng().lng.toString().replaceAll(".", ",")}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.popEstimada.toString()}</TableCell>
              <TableCell align="center" sx={{ textOverflow: "ellipsis" }} >{row.properties.areaUtil.toString().replaceAll(".", ",")}</TableCell>
              <TableCell align="center" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: "ellipsis", maxWidth: "126px" }} >{(row.properties.obs ?? "")}</TableCell>
              {isEditable &&
                <TableCell align="center" sx={{ textOverflow: "ellipsis" }}>
                  <RemoveButton
                    title="Remover Sirene?"
                    message="Deseja mesmo remover esta sirene? Esta ação é irreversível!"
                    onRemove={() => onRemove(row)}
                  />
                </TableCell>
              }
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer >
  </Box >;
};
